import { z } from 'zod';
import { contentIdSchema } from './content-id.js';
/**
 * Опыт, уровень и состояния (D3, D4, D31).
 *
 * И то и другое — шаги журнала персонажа, а не поля рядом с ним:
 * лист собирается свёрткой (D4), и «текущий опыт» в отдельной
 * колонке был бы вторым источником правды, который однажды разойдётся
 * с журналом.
 *
 * Начисляет опыт мастер (D3): он же командует уроном и лечением,
 * и опыт ничем от них не отличается. У драфта мастера нет — там
 * начисление себе, и это единственный случай, когда человек меняет
 * своё число сам.
 */
/**
 * Начисление опыта.
 *
 * Число целое и может быть отрицательным: мастер ошибся вводом,
 * и отнять начисленное должно быть так же просто, как начислить.
 * Ниже нуля общий опыт при этом не уходит — это правило свёртки,
 * а не команды.
 */
export const xpAwardSchema = z.strictObject({
    amount: z.number().int(),
});
/**
 * Опыт персонажа и то, что из него следует.
 *
 * `levelUpAvailable` — «порог взят, повышение доступно», а не
 * «уровень поднялся». Повышение остаётся решением игрока (D31):
 * оно приносит выборы — подкласс, заклинания, прибавку
 * характеристик, — и делать их за человека нельзя.
 */
export const xpStateSchema = z.strictObject({
    /** Сколько всего начислено; ниже нуля не опускается. */
    total: z.number().int().nonnegative(),
    /** Уровень по журналу: длина журнала, а не производная от опыта. */
    level: z.number().int().positive(),
    /** Уровень, на который опыта уже хватает. */
    earnedLevel: z.number().int().positive(),
    /** Следующий уровень; `null` — двадцатый достигнут. */
    nextLevel: z.number().int().positive().nullable(),
    /** Порог следующего уровня; `null` — следующего нет. */
    nextAt: z.number().int().nonnegative().nullable(),
    /** Порог взят, а повышение ещё не сделано. */
    levelUpAvailable: z.boolean(),
});
/**
 * Действие с состоянием: повесить или снять (D3).
 *
 * `level` нужен одному Истощению: оно накопительное, и «второй
 * уровень истощения» — не то же самое, что «истощён». У остальных
 * состояний уровня нет, и число там означало бы то, чего в правилах
 * не существует.
 */
export const conditionActionSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('apply'),
        condition: contentIdSchema,
        level: z.number().int().positive().nullable(),
    }),
    z.strictObject({
        kind: z.literal('remove'),
        condition: contentIdSchema,
    }),
]);
/** Состояние, висящее на персонаже сейчас. */
export const activeConditionSchema = z.strictObject({
    id: contentIdSchema,
    name: z.string().min(1),
    /** Уровень накопительного состояния; `null` — уровней у него нет. */
    level: z.number().int().positive().nullable(),
});
export const conditionProblemCodeSchema = z.enum([
    /** Записи такого состояния нет в контенте. */
    'unknown-condition',
    /** Состояние уже висит, а уровней у него нет: вешать нечего. */
    'already-applied',
    /** Снимать нечего: состояние не висит. */
    'not-applied',
    /** Уровень выше предела: «You die if your Exhaustion level is 6». */
    'level-out-of-range',
]);
export const conditionProblemSchema = z.strictObject({
    code: conditionProblemCodeSchema,
    message: z.string().min(1),
});
//# sourceMappingURL=progress.js.map